/**
 * The race, as one word-sized graphic — at FEED-ROW size.
 *
 *   race  cb▮312  gm▮▮▮1381  Δ+1069  → NEITHER
 *         │  │                 │        └ winner
 *         │  └ winning slot ms │
 *         └ per-slot bars; a struck bar ▨ = that slot errored
 *
 * "Who won the Cerebras/Gemini race, and why" is one of the two questions
 * the operator has never been able to get answered, so for the common case
 * there is NO NAVIGATION AT ALL: the answer is on the row (design §5.2).
 *
 * Seven outcomes are distinguished where the old UI collapsed them into
 * traded/not. `UNVERIFIED` — fired while the Gemini join timed out, holding
 * a live position and writing `alert: null` — is the one that would
 * otherwise read as clean, so it is the one that gets a badge.
 */

import type { RaceSummary } from "../lib/api";
import { fmtMs } from "../lib/span";

const WINNER_TITLE: Record<string, string> = {
  FAST: "the fast path answered first and claimed the post",
  SMART: "the Gemini path took the trade",
  NEITHER: "both said no — agreement, not a race loss",
  NEVER_ASKED: "a URL short-circuit made no API call; the surface will not speculate",
  NO_PARSE: "the fast lane's output did not parse",
  NO_JOIN: "the Gemini join timed out (30 s)",
  UNVERIFIED: "FIRED while the Gemini join timed out — a live position with no cover. Not clean.",
};

function Bars({ slots, winnerIdx }: { slots: RaceSummary["fast_slots"]; winnerIdx?: number | null }) {
  if (!slots || slots.length === 0) return null;
  return (
    <span className="race__slots">
      {slots.map((s, i) => (
        <span
          key={s.idx ?? i}
          className={`race__bar${s.err ? " race__bar--err" : ""}${winnerIdx === s.idx ? " race__bar--win" : ""}`}
          style={{ width: s.ms === null || s.ms === undefined ? 4 : Math.max(2, Math.min(40, s.ms / 20)) }}
          title={s.err ? `slot ${s.idx}: ${s.err}` : `slot ${s.idx}: ${fmtMs(s.ms ?? null)}`}
        />
      ))}
    </span>
  );
}

export function RaceGlyph({ race, source }: { race: RaceSummary | null | undefined; source?: string }) {
  // Five kinds of absence, five renderings (§6.7). These are two of them.
  //
  // The third — "the feed projection does not carry a race summary on this
  // build" — is a fact about the BUILD and is identical on every row, so the
  // Deck states it ONCE in `.tabs__note` rather than fifty times down the
  // feed. Nothing is claimed here in its place.
  if (race === undefined) return null;
  if (race === null || race.fast_present === 0 || race.fast_present === false) {
    return (
      <span className="race dim" title="only truth_social has a fast path today">
        race ∅ no fast path on {source ?? "this source"}
      </span>
    );
  }

  const winner = race.winner ?? "—";
  const delta = race.head_start_ms;
  return (
    <span className="race" data-winner={winner}>
      <span className="race__k">race</span>
      <span className="race__lane">
        cb
        <Bars slots={race.fast_slots} winnerIdx={race.fast_winner_idx} />
        <span className="mono">{fmtMs(race.fast_p2d_ms ?? null)}</span>
      </span>
      <span className="race__lane">
        gm
        <span
          className="race__bar"
          style={{ width: Math.max(2, Math.min(60, (race.smart_p2d_ms ?? 0) / 25)) }}
          title="smart lane: completion order, not slot identity — the smart lane does not journal a slot index"
        />
        <span className="mono">{fmtMs(race.smart_p2d_ms ?? null)}</span>
      </span>
      {delta !== null && delta !== undefined && (
        <span className="mono" title="head start: how much earlier the fast lane answered">
          Δ{delta >= 0 ? "+" : "−"}
          {fmtMs(Math.abs(delta))}
        </span>
      )}
      <span className={`race__winner${winner === "UNVERIFIED" ? " race__winner--unverified" : ""}`} title={WINNER_TITLE[winner] ?? ""}>
        → {winner}
      </span>
      {(race.fast_boundary === 1 || race.fast_boundary === true) && (
        <span className="chip" title="would_fire@80 ≠ would_fire@85">
          BOUNDARY
        </span>
      )}
    </span>
  );
}
